import { differenceInMilliseconds, formatDistanceToNow, isValid } from "date-fns";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { formatBusinessDate } from "@/shared/utils/formatters";
import { cn } from "@/shared/utils/cn";
import { type Notification } from "../types/notificationTypes";
import { AppointmentNotificationSubtitle } from "./AppointmentNotificationSubtitle";

interface NotificationCardProps {
  notification: Notification;
}

export function NotificationCard({ notification }: NotificationCardProps) {
  const { business } = useCurrentBusiness();
  const validDate = isValid(notification.createdAt);
  const timeZone = business?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const dateLabel = validDate
    ? differenceInMilliseconds(new Date(), notification.createdAt) > 2 * 24 * 60 * 60 * 1000
      ? formatBusinessDate(notification.createdAt, timeZone, {
          day: "numeric",
          month: "short",
          year: "numeric",
        })
      : formatDistanceToNow(notification.createdAt, { addSuffix: true })
    : "Date unavailable";
  const isAppointment = ["appointment", "booking"].includes(
    notification.entityType?.toLowerCase() ?? "",
  );

  return (
    <article
      aria-label={notification.title}
      className="grid min-w-0 gap-0.5 rounded-2xl border border-border bg-card px-3 py-2.5 text-card-foreground"
    >
      <h2
        className={cn(
          "text-[15px] leading-[1.3] [overflow-wrap:anywhere]",
          notification.read ? "font-normal" : "font-bold",
        )}
      >
        {notification.title}
      </h2>
      {isAppointment && notification.entityId ? (
        <AppointmentNotificationSubtitle
          appointmentId={notification.entityId}
          fallback={notification.message}
        />
      ) : (
        <p className="truncate text-xs leading-normal text-muted-foreground" title={notification.message}>
          {notification.message}
        </p>
      )}
      <time
        dateTime={validDate ? notification.createdAt.toISOString() : undefined}
        className="text-xs leading-normal text-muted-foreground"
      >
        {dateLabel}
      </time>
    </article>
  );
}
